





































import { uploadService } from 'common-module/src/util/upload';
import { getEncodedAuthImageUrl } from 'common-module/src/util/authImage';
import { composePath } from 'common-module/src/util/composePath';
import { useInstance } from 'common-module/src/util/useInstance';
import { openVideoDialog } from 'common-module/src/components/video';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  PropType
} from '@vue/composition-api';
import { onKeyDown, templateRef, useElementBounding } from '@vueuse/core';
import axios from 'axios';
import { pick } from 'lodash';
import { enUS, UploaderResultItem, ZKEditor, ZKEditorContent } from 'zk-editor';
import 'zk-editor/dist/style.css';
import { emitter } from 'zk-ui';
import { BindOption } from 'common-module/src/util/upload/model';

const getFileSuffix = (fileUrl: string) => {
  return fileUrl?.substring(fileUrl.lastIndexOf('.'));
};

const getPlantumlUrl = () => {
  const host = window.location.host;
  if (host.includes('localhost') || host.includes('.net')) {
    return 'https://pre.leiga.net/plantuml/svg/';
  }
  if (host.includes('.cn')) {
    return 'https://ligai.cn/plantuml/svg/';
  }
  if (host.includes('qa')) {
    return 'https://pre.leigaqa.com/plantuml/svg/';
  }
  return 'https://pre.leiga.net/plantuml/svg/';
};

const isImage = (name: string) => {
  return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.ico'].includes(
    getFileSuffix(name)
  );
};

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/x-icon': 'ico',
  'image/bmp': 'bmp',
  'image/tiff': 'tiff'
};

const base64ToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const ext = MIME_TO_EXT[mime] || 'png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], `${filename}.${ext}`, { type: mime });
};

export default defineComponent({
  components: { ZKEditor },
  mixins: [emitter],
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => ({})
    },
    uploadOptions: {
      type: Object as PropType<BindOption>,
      required: true
    },
    openVideoDialog: {
      type: Function,
      default: () => {}
    }
  },
  setup(props, { emit }) {
    const inEdit = ref(false);
    const editorRef = templateRef<any>('editor');
    const instance = useInstance();

    let savedContent = props.value;
    onMounted(() => {
      const content = editorRef.value?.content as ZKEditorContent;
      emit('mounted', content);
      const html = content.getHTML();
      savedContent = html;
    });

    const uploadImage = async (file: File) => {
      const data = new FormData();
      data.append('files', file, file.name.replace(/\s/g, '_'));

      try {
        const res = await uploadService.uploadFileWithBind(
          {
            file
          },
          props.uploadOptions
        );
        return pick(res, ['fileName', 'fileType', 'originalUrl', 'fileSize']);
      } catch {
        return null;
      }
    };

    const onRequestUpload = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.onchange = async (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        const content = editorRef.value.content as ZKEditorContent;
        content.commands.uploadImages(files);
      };

      input.click();
    };

    const stopEdit = () => {
      if (inEdit.value) {
        inEdit.value = false;
        emit('blur');
      }
    };

    const saveContent = () => {
      if (editorRef.value?.content && !props.disabled) {
        const content = editorRef.value.content as ZKEditorContent;
        let html = content.getHTML();
        if (html === '<p></p>' && savedContent !== '<p></p>') {
          html = '';
        }
        if (html !== savedContent) {
          savedContent = html;
          emit('change', html);
          emit('flushChange');
          instance.dispatch?.('ElFormItem', 'el.form.blur', [html]);
        }
      }
    };

    const stop = watch(
      () => editorRef.value,
      () => {
        if (editorRef.value) {
          stop();
          setTimeout(() => {
            if (!editorRef.value) {
              return;
            }
            const content = editorRef.value.content as ZKEditorContent;
            if (content && !props.disabled) {
              content.on('update', () => {
                if (!inEdit.value) {
                  saveContent();
                }
              });

              editorRef.value.$el.addEventListener('paste', handleBase64Paste, true);

              const toolbar = editorRef.value.$el.querySelector(
                '.zk-editor-toolbar'
              ) as HTMLDivElement;
              toolbar?.addEventListener('click', () => {
                setTimeout(() => {
                  if (!content.isFocused) {
                    saveContent();
                  }
                });
              });
              setTimeout(() => {
                content.setEditable(true);
              });
            }
          }, 0);
        }
      }
    );

    onKeyDown(
      'Escape',
      (e) => {
        if (
          document.querySelector('.viewer-container[role="dialog"]') &&
          !props.disabled
        ) {
          if (inEdit.value) {
            const content = editorRef.value.content as ZKEditorContent;
            content.commands.focus();
          }
          return;
        }
        if (editorRef.value.$el.classList.contains('fullscreen')) {
          editorRef.value.onFullScreen();
        }
        if (inEdit.value) {
          const content = editorRef.value.content as ZKEditorContent;
          content.commands.blur();
          stopEdit();
          saveContent();
          e.stopPropagation();
        }
      },
      {
        passive: true
      }
    );

    const uploader = async (file: File): Promise<UploaderResultItem> => {
      if (!isImage(file.name)) {
        instance.$message.warning(instance.$t('project.pictureClip.fileType'));

        throw new Error('不支持的格式');
      }

      if (file.size > 20 * 1024 * 1024) {
        instance.$message.warning(
          instance.$t('fileMaxSizeMessage', { size: '20M' })
        );
        throw new Error('不支持的格式');
      }

      const res = await uploadImage(file);
      if (res === null) {
        throw new Error('上传失败');
      }
      return {
        url: res.originalUrl,
        name: res.fileName,
        type: res.fileType,
        size: res.fileSize
      };
    };

    const SUPPORTED_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.ico'];

    const handleBase64Paste = async (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData || clipboardData.files.length > 0) {
        return;
      }

      const html = clipboardData.getData('text/html');
      if (!html || !html.includes('data:image')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const content = editorRef.value?.content as ZKEditorContent;
      if (!content) {
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const base64Images = Array.from(doc.querySelectorAll('img[src^="data:"]'));

      if (base64Images.length === 0) {
        content.commands.insertContent(html);
        return;
      }

      await Promise.all(base64Images.map(async (img, index) => {
        const dataUrl = img.getAttribute('src') || '';
        try {
          const file = base64ToFile(dataUrl, `pasted-image-${Date.now()}-${index}`);
          const fileExt = getFileSuffix(file.name);
          if (!SUPPORTED_IMAGE_EXTS.includes(fileExt) || file.size > 20 * 1024 * 1024) {
            throw new Error('unsupported');
          }
          const res = await uploadImage(file);
          if (res && res.originalUrl) {
            img.setAttribute('src', res.originalUrl);
          } else {
            throw new Error('upload failed');
          }
        } catch {
          const span = doc.createElement('span');
          span.textContent = `[${instance.$t('kanban.imageSaveFailed')}]`;
          span.style.color = '#999';
          img.replaceWith(span);
        }
      }));

      content.commands.insertContent(doc.body.innerHTML);
    };

    // const getPlantumlSvg = async (code: string) => {
    //   const client = axios.create({});
    //   const res = await client.get(getPlantumlUrl() + code, {});
    //   return res.data;
    // };

    watch(
      () => props.value,
      () => {
        if (editorRef.value && savedContent !== props.value) {
          const content = editorRef.value.content as ZKEditorContent;
          content.commands.loadContent(props.value);
        }
      }
    );

    const { height: contentHeight } = useElementBounding(
      computed(() => editorRef.value?.$el.querySelector('.ProseMirror'))
    );

    const isExpand = ref(false);
    const isCanExpand = computed(
      () => !inEdit.value && contentHeight.value > Math.max(200, window.innerHeight * 0.4)
    );

    const toggleExpand = (e: MouseEvent) => {
      e.stopPropagation();
      isExpand.value = !isExpand.value;
      if (!isExpand.value) {
        setTimeout(() => {
          (
            instance.$el as HTMLDivElement
          ).parentElement!.parentElement!.scrollIntoView({
            behavior: 'smooth'
          });
        });
      }
    };

    watch(
      () => [!props.disabled && inEdit.value, editorRef.value],
      ([editable]) => {
        if (editorRef.value) {
          const content = editorRef.value.content as ZKEditorContent;
          content.setEditable(editable);
        }
      },
      {
        immediate: true
      }
    );

    const onCopySuccess = () => {
      instance.$message.success(instance.$t('message.copySuccess'));
    };

    const onEditorFocus = () => {
      const content = editorRef.value.content as ZKEditorContent;
      content.view.focus();
      inEdit.value = true;
    };

    onBeforeUnmount(() => {
      if (editorRef.value?.$el) {
        editorRef.value.$el.removeEventListener('paste', handleBase64Paste, true);
      }
    });

    return {
      isExpand,
      toggleExpand,
      isCanExpand,
      inEdit,
      stopEdit,
      saveContent,
      editorOptions: computed(() => ({
        toolbar: true,
        uploader,
        // getPlantumlSvg,
        placeholder: props.placeholder,
        editable: !props.disabled,
        i18n: instance.$i18n.locale === 'zh' ? undefined : { ...enUS, 'fullscreen': 'Full-screen' },
        imageUrlHandler: (src: string) => getEncodedAuthImageUrl(src),
        openVideoDialog,
        transcriptBtnText: instance.$t('editor.recorder.transcript'),
        transcriptBtnTips: instance.$t('editor.recorder.transcriptTips'),
        ...props.options
      })),
      startEdit: (e: MouseEvent) => {
        if (!inEdit.value && editorRef.value) {
          const eventTargetPath = Array.from(
            e.composedPath() || composePath(e.target as HTMLElement) || []
          );

          if (
            eventTargetPath.some(
              (el) => el instanceof HTMLElement
              && (el.hasAttribute?.('data-editor-image') || el.classList.contains('recorder-pay-btn'))
            )
          ) {
            return;
          }

          if (!props.disabled) {
            inEdit.value = true;
            isExpand.value = true;
          }
        }
      },
      onClickOutside(e?: MouseEvent) {
        const templateSaveDialog = document.querySelector(
          '.template-save-dialog'
        ) as HTMLElement | undefined;
        if (
          e &&
          inEdit.value &&
          !Array.from(
            e.composedPath() || composePath(e.target as HTMLElement) || []
          ).some(
            (el: any) => el.classList?.contains('tippy-box') ||
              el.classList?.contains('tippy-content') ||
              el.classList?.contains('template-item') ||
              el.classList?.contains('el-popper')
          ) &&
          !document.querySelector('.zk-editor.fullscreen') &&
          (!templateSaveDialog || templateSaveDialog.style.display === 'none')
        ) {
          stopEdit();
          saveContent();
        }

        if (inEdit.value && !e) {
          stopEdit();
          saveContent();
        }
      },
      onRequestUpload,
      onTemplateSaveClose() {
        const content = editorRef.value.content as ZKEditorContent;
        content.view.focus();
      },
      defaultContent: props.value,
      onCopySuccess,
      onEditorFocus
    };
  }
});
